/*
 * Digital catalog (itch.io link-out) page.
 *
 * Fetches the public, curated catalog blob from our own read endpoint and
 * renders native-looking product cards that link out to itch.io in a new tab.
 * The page never contacts itch directly — it only reads the cached, sanitized
 * list (visible titles, in saved order) served by our API.
 */
(function () {
  var root = document.querySelector('[data-section="digital-catalog"]');
  if (!root) return;

  var endpoint = root.getAttribute('data-endpoint');
  var profileUrlSetting = root.getAttribute('data-itch-profile-url');
  var list = root.querySelector('[data-digital-items]');
  var empty = root.querySelector('[data-digital-empty]');

  // Time between each card fading in — quick, staggered "1 by 1" reveal.
  var ENTER_STEP_MS = 70;
  var viewMore = root.querySelector('[data-digital-view-more]');
  var viewMoreLink = root.querySelector('[data-digital-view-more-link]');

  var EMPTY_TEXTS = [
    "The goblin was sent to fetch the digital wares.<br>He returned with a wet ledger and no explanation.",
    "A goblin guards the shelf where the downloads should be.<br>The shelf is empty. He insists this is temporary.",
    "The stall is bare for now.<br>The goblin swears the good stuff is still drying in the cistern."
  ];

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  function formatPrice(price) {
    // price is { amount: <minor units / cents>, currency: <ISO code> }.
    // amount 0 (or missing) means pay-what-you-want on itch.
    if (!price || !price.amount) return 'Name your price';
    var amount = Number(price.amount);
    if (!isFinite(amount) || amount <= 0) return 'Name your price';
    var currency = price.currency || 'USD';

    // Always spell out the currency code. These are fixed itch prices, so they
    // do NOT follow the store's country/currency selector the way Shopify
    // products do. Left to the browser locale, Intl renders AUD as a bare "$"
    // for an Australian visitor and "A$" for an American one, so the same page
    // showed "$10.00" with nothing saying which dollar it meant.
    //
    // narrowSymbol keeps it "$10.00 AUD" rather than "A$10.00 AUD"; it throws
    // on older Safari, hence the widening fallbacks.
    var formatted;
    try {
      formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol'
      }).format(amount / 100);
    } catch (e) {
      try {
        formatted = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency
        }).format(amount / 100);
      } catch (e2) {
        formatted = (amount / 100).toFixed(2);
      }
    }

    // Don't repeat the code if the locale already rendered it (currencyDisplay
    // falls back to the code itself for currencies with no known symbol).
    return formatted.indexOf(currency) === -1 ? formatted + ' ' + currency : formatted;
  }

  // Titles are named for itch SEO, e.g. "Utility - Art Pack | Dark Fantasy" or
  // "Adventure - An Exodus || Compatible with Mörk Borg". Show just the middle:
  // drop a trailing "compatible with..." suffix (from the first | or /) and a
  // leading "Category - " prefix (the first space-hyphen-space, so hyphenated
  // words survive). The full name is kept in the data and on link hover.
  function trimTitle(raw) {
    var t = String(raw || '').trim();
    if (!t) return 'Untitled';
    var suffix = t.search(/\s*[|/]/);
    if (suffix !== -1) t = t.slice(0, suffix);
    var dash = t.indexOf(' - ');
    if (dash !== -1) t = t.slice(dash + 3);
    t = t.trim();
    return t || String(raw).trim();
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  // External-link icon with a visually-hidden label so screen readers announce
  // that the link leaves the site (G14).
  function externalIcon() {
    var span = el('span', 'digital-card__external');
    span.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M15 3h6v6"/><path d="M10 14 21 3"/>' +
      '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>' +
      '<span class="visually-hidden">(opens in a new window)</span>';
    return span;
  }

  function renderCard(item) {
    var li = el('li', 'digital-card');

    var displayTitle = trimTitle(item.title);

    var link = el('a', 'digital-card__link');
    link.href = item.itchUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    if (item.title) link.title = item.title; // full SEO name on hover

    var media = el('span', 'digital-card__media');
    if (item.s3ImageUrl) {
      var img = el('img', 'digital-card__image');
      img.src = item.s3ImageUrl;
      img.alt = displayTitle;
      img.loading = 'lazy';
      media.appendChild(img);
    } else {
      media.className += ' digital-card__media--no-image';
    }
    link.appendChild(media);

    var body = el('span', 'digital-card__body');

    var titleRow = el('span', 'digital-card__title');
    var titleText = el('span', 'digital-card__title-text');
    titleText.textContent = displayTitle;
    titleRow.appendChild(titleText);
    titleRow.appendChild(externalIcon());
    body.appendChild(titleRow);

    if (item.blurb) {
      var blurb = el('span', 'digital-card__blurb');
      blurb.textContent = item.blurb;
      body.appendChild(blurb);
    }

    var price = el('span', 'digital-card__price');
    price.textContent = formatPrice(item.price);
    body.appendChild(price);

    link.appendChild(body);
    li.appendChild(link);
    return li;
  }

  function clearSkeletons() {
    var sks = list.querySelectorAll('.digital-card--skeleton');
    for (var i = 0; i < sks.length; i++) sks[i].remove();
  }

  function render(items) {
    if (!items || !items.length) {
      clearSkeletons();
      hide(list);
      var text = empty && empty.querySelector('[data-digital-empty-text]');
      if (text) text.innerHTML = EMPTY_TEXTS[Math.floor(Math.random() * EMPTY_TEXTS.length)];
      show(empty);
      return;
    }

    var skeletons = list.querySelectorAll('.digital-card--skeleton');

    items.forEach(function (item, i) {
      var card = renderCard(item);
      card.className += ' digital-card--enter';
      // Stagger: each card takes over its skeleton's slot, then fades in.
      setTimeout(function () {
        var sk = skeletons[i];
        if (sk && sk.parentNode === list) {
          list.replaceChild(card, sk);
        } else {
          list.appendChild(card);
        }
        // Two rAFs so the starting (opacity 0) state is committed before the
        // transition to visible runs.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            card.classList.add('is-visible');
          });
        });
      }, i * ENTER_STEP_MS);
    });

    // Fade out and remove any skeletons beyond the number of real cards.
    setTimeout(function () {
      var leftover = list.querySelectorAll('.digital-card--skeleton');
      for (var j = 0; j < leftover.length; j++) leftover[j].classList.add('is-leaving');
      setTimeout(clearSkeletons, 320);
    }, items.length * ENTER_STEP_MS + 20);

    showViewMore(items);
  }

  // "See everything on itch" button below the grid. Uses the configured profile
  // URL, or falls back to the origin of the first title's itch link.
  function showViewMore(items) {
    if (!viewMore || !viewMoreLink) return;
    var url = profileUrlSetting;
    if (!url && items.length) {
      try { url = new URL(items[0].itchUrl).origin; } catch (e) { url = ''; }
    }
    if (!url) return;
    viewMoreLink.href = url;
    show(viewMore);
  }

  function load() {
    if (!endpoint) {
      // Not configured — degrade to the empty state rather than a broken page.
      render([]);
      return;
    }
    // The API's custom-domain mapping key strips the base path, so a bare
    // ".../catalog" resolves to an empty route (404). Normalise to a single
    // trailing slash so ".../catalog" and ".../catalog/" both hit GET /.
    var url = endpoint.replace(/\/+$/, '') + '/';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Bad response: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        // Accept either a bare array or { titles: [...] }.
        var items = Array.isArray(data) ? data : (data && data.titles) || [];
        render(items);
      })
      .catch(function () {
        // API down or unreachable — same graceful empty state.
        render([]);
      });
  }

  load();
})();
